import { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { IconButton, Search, SkeletonText } from '@carbon/react';
import {
  Location,
  List,
  Table,
  ExpandAll,
  CollapseAll,
} from '@carbon/react/icons';
import { PropertyCard } from './property-card';
import { ComparisonPropertiesTable } from './comparison-properties-table';
import { format0 } from 'utils/number';
import { colorRulesAtom, propertyFiltersAtom } from './atoms';
import { applyPropertyFilters } from './property-filters';
import { useAoiFilteredUwis } from './hooks';
import type { EconomicsPair } from './property-attributes';
import type { ComparisonResult } from './types';
import style from './comparison-properties.module.scss';

type ViewMode = 'card' | 'table';

/**
 * True when a property matches the search term by its id (UWI or unique slot id)
 * or its well name. An empty term matches everything.
 * @param {ComparisonResult} result - The property comparison result
 * @param {string} term - The current search term
 * @return {boolean} Whether the property matches
 */
const matchesPropertySearch = (
  result: ComparisonResult,
  term: string
): boolean => {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  const id = (result.uwi ?? result.uniqueSlotId ?? '').toLowerCase();
  const name = (result.wellName ?? '').toLowerCase();
  return id.includes(q) || name.includes(q);
};

interface ComparisonPropertiesProps {
  results: ComparisonResult[];
  isLoading: boolean;
  /** Both sides' scenario economics, for the per-unit economic rows. */
  economics: EconomicsPair | null;
}

/**
 * "Properties (N)" section (mock v16): a toolbar (search by property id/name,
 * Show all maps, card / table view, expand attributes) over one comparison card
 * per property, or a melted table. Cards show Origin vs Target attributes with
 * deltas and can flip to side-by-side maps. Export lives on the global (header)
 * action only.
 * @param {ComparisonPropertiesProps} props - The results and loading flag
 * @return {JSX.Element} The Properties section
 */
export const ComparisonProperties = ({
  results,
  isLoading,
  economics,
}: ComparisonPropertiesProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [showAllMaps, setShowAllMaps] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Section-wide attribute expansion: false = only differences (default), true =
  // all attributes. The nonce re-applies the choice to cards that were toggled
  // individually.
  const [expandAll, setExpandAll] = useState(false);
  const [expandNonce, setExpandNonce] = useState(0);
  const applyExpand = (value: boolean) => {
    setExpandAll(value);
    setExpandNonce((n) => n + 1);
  };

  const propertyFilters = useAtomValue(propertyFiltersAtom);
  const colorRules = useAtomValue(colorRulesAtom);
  const tableRules = useMemo(
    () => colorRules.filter((r) => r.enabled && r.applyToTables),
    [colorRules]
  );

  // Global Map Filter → Draw AOI: scope the Properties list to slots whose well
  // falls inside the drawn polygon. The in-AOI UWI set is resolved by the same
  // backend spatial query the Type Curve page uses (Elasticsearch geo_shape via
  // /queries/wells), so it is exact and has no well-count cap. Extent mode still
  // only affects the map.
  const { uwis: aoiUwis } = useAoiFilteredUwis(results);
  const aoiScoped = useMemo(() => {
    if (!aoiUwis) return results;
    return results.filter((r) => r.uwi && aoiUwis.has(r.uwi));
  }, [results, aoiUwis]);

  const filtered = useMemo(
    () => applyPropertyFilters(aoiScoped, propertyFilters, economics),
    [aoiScoped, propertyFilters, economics]
  );
  const searched = useMemo(
    () => filtered.filter((r) => matchesPropertySearch(r, searchTerm)),
    [filtered, searchTerm]
  );

  if (isLoading) {
    return (
      <section className={style.section}>
        <h2 className={style.heading}>Properties</h2>
        <div className={style.card}>
          <SkeletonText paragraph lineCount={4} />
        </div>
      </section>
    );
  }

  const count = searched.length;

  return (
    <section className={style.section}>
      <h2 className={style.heading}>Properties ({format0(count)})</h2>

      <div className={style.toolbar}>
        <Search
          className={style.search}
          size="lg"
          labelText="Search properties"
          placeholder="Search by property id or name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm('')}
        />
        <div className={style.toolbarActions}>
          <IconButton
            kind="ghost"
            label="Show all maps"
            align="bottom"
            isSelected={showAllMaps}
            onClick={() => setShowAllMaps((on) => !on)}
          >
            <Location />
          </IconButton>
          <IconButton
            kind="ghost"
            label={expandAll ? 'Show only differences' : 'Show all attributes'}
            align="bottom"
            isSelected={expandAll}
            onClick={() => applyExpand(!expandAll)}
          >
            {expandAll ? <CollapseAll /> : <ExpandAll />}
          </IconButton>
          <IconButton
            kind="ghost"
            label={viewMode === 'card' ? 'Table view' : 'Card view'}
            align="bottom"
            onClick={() =>
              setViewMode((mode) => (mode === 'card' ? 'table' : 'card'))
            }
          >
            {viewMode === 'card' ? <Table /> : <List />}
          </IconButton>
        </div>
      </div>

      {viewMode === 'table' ? (
        <ComparisonPropertiesTable
          results={searched}
          expandAll={expandAll}
          tableRules={tableRules}
          economics={economics}
        />
      ) : (
        searched.map((result) => (
          <PropertyCard
            key={result.uniqueSlotId}
            result={result}
            showAllMaps={showAllMaps}
            expandAll={expandAll}
            expandNonce={expandNonce}
            tableRules={tableRules}
            economics={economics}
          />
        ))
      )}

      {count > 0 && (
        <p className={style.footer}>
          1–{format0(count)} of {format0(count)} Properties
        </p>
      )}
    </section>
  );
};
